export interface MusicTrack {
  id: string
  path: string
  displayName: string
  fileName: string
  size: number
}

interface AssetManifest {
  audio?: unknown
}

const MAX_TRACKS = 500
const MAX_TRACK_BYTES = 500 * 1024 * 1024

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function requireString(value: unknown, field: string, maxLength: number): string {
  if (typeof value !== 'string' || value.length === 0 || value.length > maxLength) {
    throw new Error(`音乐清单中的 ${field} 无效。`)
  }
  return value
}

export function parseMusicCatalog(value: unknown): MusicTrack[] {
  if (!isRecord(value)) throw new Error('音乐资源清单格式无效。')
  const manifest = value as AssetManifest
  if (manifest.audio === undefined) return []
  if (!Array.isArray(manifest.audio) || manifest.audio.length > MAX_TRACKS) {
    throw new Error(`音乐清单最多允许 ${MAX_TRACKS} 首音乐。`)
  }

  const ids = new Set<string>()
  const paths = new Set<string>()
  return manifest.audio.map((entry, index) => {
    if (!isRecord(entry)) throw new Error(`音乐清单第 ${index + 1} 项无效。`)
    const id = requireString(entry.id, `audio[${index}].id`, 80)
    const path = requireString(entry.path, `audio[${index}].path`, 600)
    const displayName = requireString(entry.displayName, `audio[${index}].displayName`, 200)
    const fileName = requireString(entry.fileName, `audio[${index}].fileName`, 260)
    const size = entry.size
    if (
      !/^music\/[^/]+\.mp3(?:\?v=[a-f0-9]{1,64})?$/i.test(path) ||
      path.includes('..') ||
      path.includes('\\')
    ) {
      throw new Error(`音乐清单第 ${index + 1} 项路径无效。`)
    }
    if (
      typeof size !== 'number' ||
      !Number.isSafeInteger(size) ||
      size < 0 ||
      size > MAX_TRACK_BYTES
    ) {
      throw new Error(`音乐清单第 ${index + 1} 项大小无效。`)
    }
    if (ids.has(id) || paths.has(path)) throw new Error('音乐清单包含重复项目。')
    ids.add(id)
    paths.add(path)
    return { id, path, displayName, fileName, size }
  })
}

export function musicTrackUrl(track: MusicTrack, origin: string, baseUrl: string): string {
  const base = new URL(baseUrl, origin)
  return new URL(`assets/${track.path}`, base).href
}
